import { Chip, DataTableMeta, DateColumn, LimitedContent } from '@otp/ui';
import { TFunction } from 'i18next';
import {
  IssueTicketImportance,
  IssueTicketStatus,
  Maybe,
  StoreModel,
  Tag,
  User,
} from 'src/graphql/generated/graphql';
import { getFullName } from 'src/utils/user-data';
import { formatIssueTicketId } from '../../utils/utils';
import { ImportanceField } from './columns/importance';
import { StoresField } from 'src/components/common/table/columns/stores';
import { ActionsColumn } from './columns/actions';
import { TagsField } from './columns/tags';

export type IssueTicket = {
  id: string;
  autoincrementId?: number;
  name: string;
  status: IssueTicketStatus;
  importance: IssueTicketImportance;
  description: string;
  createdAt?: Date;
  updatedAt?: Date;
  stores: Partial<StoreModel>[];
  createdBy: Partial<User>;
  assignedTo?: Maybe<Partial<User>>;
};

export const getTableConfig = (data: IssueTicket[], { t }: { t: TFunction }) => [
  {
    name: 'idNumber',
    label: t('columns.id'),
    options: {
      sort: true,
      customBodyRender: (idNumber: number) => (
        <Chip label={formatIssueTicketId({ idNumber }, false)} />
      ),
    },
  },
  {
    name: 'name',
    label: t('columns.name'),
    options: {
      sort: true,
      setCellProps: () => ({ className: '!max-w-sm' }),
    },
  },
  {
    name: 'stores',
    label: t('columns.store'),
    options: {
      isDesktopOnly: true,
      customBodyRender: (value: Partial<StoreModel>[]) => (
        <StoresField stores={value} />
      ),
    },
  },
  {
    name: 'importance',
    label: t('columns.importance'),
    options: {
      sort: true,
      isDesktopOnly: true,
      customBodyRender: (value: IssueTicketImportance) => (
        <ImportanceField importance={value} />
      ),
    },
  },
  {
    name: 'tags',
    label: t('columns.tags'),
    options: {
      isDesktopOnly: true,
      customBodyRender: (tags: Tag[]) => <TagsField tags={tags} />,
    },
  },
  {
    name: 'description',
    label: t('columns.description'),
    options: {
      isDesktopOnly: true,
      customBodyRender: (value: string) => (
        <LimitedContent maxRows={3}>
          <div dangerouslySetInnerHTML={{ __html: value }} />
        </LimitedContent>
      ),
      setCellProps: () => ({ className: 'max-w-lg' }),
    },
  },
  {
    name: 'createdAt',
    label: t('columns.created-at'),
    options: {
      sort: true,
      isDesktopOnly: true,
      customBodyRender: (value: string) => <DateColumn date={value} />,
    },
  },
  {
    name: 'updatedAt',
    label: t('columns.updated-at'),
    options: {
      sort: true,
      isDesktopOnly: true,
      customBodyRender: (value: string) => <DateColumn date={value} />,
    },
  },
  {
    name: 'createdBy',
    label: t('columns.created-by'),
    options: {
      isDesktopOnly: true,
      customBodyRender: (user: Partial<User>) => getFullName(user, ' '),
    },
  },
  {
    name: 'assignedTo',
    label: t('columns.assigned-to'),
    options: {
      isDesktopOnly: true,
      customBodyRender: (user: Partial<User>) => getFullName(user, ' '),
    },
  },
  {
    name: 'actions',
    label: t('columns.actions'),
    options: {
      customBodyRender: (_: any, column: DataTableMeta) => {
        const issueTicket = data[column.rowIndex];
        return <ActionsColumn issueTicket={issueTicket} />;
      },
    },
  },
];
